import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { EmptyState } from './EmptyState';
import { FavoritesTab } from './FavoritesTab';
import { useFavorites } from '../contexts/FavoritesContext';

export function ProfileTabs({ user, reservations = [] }) {
  const [activeTab, setActiveTab] = useState('favorites');
  const { favorites } = useFavorites();

  const tabs = [
    { id: 'favorites', label: 'Favorites', count: favorites.length },
    { id: 'recommendations', label: 'Recommendations' },
    { id: 'votes', label: 'My Votes', count: 0 },
    { id: 'reservations', label: 'My Reservations', count: reservations.length },
    { id: 'achievements', label: 'Achievements & Challenges' },
  ];

  return (
    <div className="mt-16">
      {/* Tab Navigation */}
      <div className="flex items-center gap-10 border-b border-border mb-12">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                relative flex items-center gap-3 pb-4 transition-colors text-sm uppercase tracking-widest
                ${isActive 
                  ? 'text-foreground' 
                  : 'text-muted-foreground hover:text-foreground'
                }
              `}
            >
              <span>{tab.label}</span>
              {tab.count !== undefined && (
                <span className="text-xs opacity-60">
                  ({tab.count})
                </span>
              )}
              
              {/* Active indicator */}
              {isActive && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-[2px] bg-foreground"
                  transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4 }}
        >
          {activeTab === 'favorites' && (
            <div>
              <FavoritesTab />
            </div>
          )}
          
          {activeTab === 'recommendations' && (
            <div>
              <EmptyState
                title="No recommendations available"
                description="We'll show you personalized dish recommendations based on your tastes and ratings once you start rating dishes."
              />
            </div>
          )}
          
          {activeTab === 'votes' && (
            <div>
              <EmptyState
                title="No votes recorded"
                description="Start rating dishes to see your voting history here. Your opinions help others discover great food."
              />
            </div>
          )}
          
          {activeTab === 'reservations' && (
            <div>
              <ReservationsTabComponent reservations={reservations} />
            </div>
          )}
          
          {activeTab === 'achievements' && (
            <div>
              <FoodieChallengesComponent />
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// Placeholder components
function ReservationsTabComponent({ reservations = [] }) {
  if (reservations.length === 0) {
    return (
      <EmptyState
        title="No reservations yet"
        description="You haven't made any reservations yet. Browse restaurants and book a table to see them here."
        actionLabel="Browse Restaurants"
        onAction={() => window.location.href = '/'}
      />
    );
  }

  return (
    <div className="space-y-4">
      {reservations.map((reservation) => (
        <div key={reservation.id} className="border border-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="serif text-2xl mb-2">{reservation.restaurantName}</h3>
              <p className="text-sm text-muted-foreground">
                {new Date(reservation.date).toLocaleDateString()} at {reservation.time}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Party of {reservation.partySize}
              </p>
            </div>
            <div className="text-right">
              <span className={`text-xs px-3 py-1 border ${
                reservation.status === 'confirmed' 
                  ? 'border-green-600 text-green-600' 
                  : reservation.status === 'cancelled'
                  ? 'border-red-600 text-red-600'
                  : 'border-amber-600 text-amber-600'
              }`}>
                {reservation.status}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function FoodieChallengesComponent() {
  return (
    <EmptyState
      title="No achievements yet"
      description="Complete challenges and rate dishes to unlock achievements and badges."
      actionLabel="Start Rating"
      onAction={() => window.location.href = '/'}
    />
  );
}

